/**
 * WorkOrderVendors — Vendor management page
 *
 * CRUD for external vendors/contractors that can be assigned to work orders.
 */

import { useEffect, useState, useCallback } from "react";
import {
	Table,
	Button,
	Modal,
	Input,
	InputNumber,
	message,
	Popconfirm,
	Tag,
	Space,
} from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import {
	listVendors,
	createVendor as apiCreateVendor,
	updateVendor as apiUpdateVendor,
	deleteVendor as apiDeleteVendor,
	type Vendor,
} from "@/services/work-order-service";

const WorkOrderVendors = () => {
	const [vendors, setVendors] = useState<Vendor[]>([]);
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
	const [form, setForm] = useState({
		name: "",
		contactName: "",
		contactEmail: "",
		contactPhone: "",
		specialty: "",
		hourlyRate: 0,
	});

	const fetchVendors = useCallback(async () => {
		setLoading(true);
		try {
			const result = await listVendors(true);
			setVendors(result.vendors ?? []);
		} catch {
			message.error("Failed to load vendors");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => { fetchVendors(); }, [fetchVendors]);

	const openCreate = () => {
		setEditingVendor(null);
		setForm({ name: "", contactName: "", contactEmail: "", contactPhone: "", specialty: "", hourlyRate: 0 });
		setModalOpen(true);
	};

	const openEdit = (v: Vendor) => {
		setEditingVendor(v);
		setForm({
			name: v.name,
			contactName: v.contactName ?? "",
			contactEmail: v.contactEmail ?? "",
			contactPhone: v.contactPhone ?? "",
			specialty: v.specialty ?? "",
			hourlyRate: v.hourlyRate ?? 0,
		});
		setModalOpen(true);
	};

	const handleSave = async () => {
		if (!form.name.trim()) {
			message.warning("Name is required");
			return;
		}
		try {
			if (editingVendor) {
				await apiUpdateVendor(editingVendor.id, form);
			} else {
				await apiCreateVendor(form);
			}
			message.success(editingVendor ? "Vendor updated" : "Vendor created");
			setModalOpen(false);
			await fetchVendors();
		} catch {
			message.error("Failed to save vendor");
		}
	};

	const handleDelete = async (id: number) => {
		try {
			await apiDeleteVendor(id);
			message.success("Vendor deactivated");
			await fetchVendors();
		} catch {
			message.error("Failed to delete vendor");
		}
	};

	return (
		<div style={{ padding: 20 }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
				<h2 style={{ margin: 0 }}>Vendors</h2>
				<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
					Add Vendor
				</Button>
			</div>

			<Table
				dataSource={vendors}
				rowKey="id"
				loading={loading}
				size="middle"
				columns={[
					{ title: "Name", dataIndex: "name", key: "name", ellipsis: true },
					{ title: "Contact", dataIndex: "contactName", key: "contactName" },
					{ title: "Email", dataIndex: "contactEmail", key: "contactEmail" },
					{ title: "Phone", dataIndex: "contactPhone", key: "contactPhone" },
					{ title: "Specialty", dataIndex: "specialty", key: "specialty" },
					{
						title: "Rate",
						dataIndex: "hourlyRate",
						key: "hourlyRate",
						render: (v: number | null) => (v ? `$${v}/hr` : "—"),
					},
					{
						title: "Status",
						dataIndex: "isActive",
						key: "isActive",
						render: (v: boolean) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag>,
					},
					{
						title: "Actions",
						key: "actions",
						width: 100,
						render: (_: unknown, record: Vendor) => (
							<Space>
								<Button type="text" icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
								<Popconfirm title="Deactivate this vendor?" onConfirm={() => handleDelete(record.id)}>
									<Button type="text" danger icon={<DeleteOutlined />} size="small" />
								</Popconfirm>
							</Space>
						),
					},
				]}
			/>

			<Modal
				open={modalOpen}
				onCancel={() => setModalOpen(false)}
				title={editingVendor ? "Edit Vendor" : "Add Vendor"}
				onOk={handleSave}
				okText="Save"
			>
				<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
					<div><label>Name *</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
					<div><label>Contact Name</label><Input value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} /></div>
					<div><label>Email</label><Input value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} /></div>
					<div><label>Phone</label><Input value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} /></div>
					<div><label>Specialty</label><Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} /></div>
					<div><label>Hourly Rate ($)</label><InputNumber value={form.hourlyRate} onChange={(v) => setForm({ ...form, hourlyRate: v ?? 0 })} style={{ width: "100%" }} min={0} /></div>
				</div>
			</Modal>
		</div>
	);
};

export { WorkOrderVendors };
